import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  Button, 
  Paper,
  Divider,
  Fade,
  Grow,
  Zoom
} from '@mui/material';
import { 
  Home, 
  DirectionsCar, 
  Agriculture, 
  School,
  Business,
  Receipt,
  Calculate,
  TrendingUp,
  ArrowForward,
  Phone,
  Email,
  LocationOn,
  CheckCircle
} from '@mui/icons-material';

// Import images
import loansHero from '../images/x.jpg';
import homeLoanImage from '../images/platinum.jpg';
import agriLoanImage from '../images/b1.png';
import vehicleLoanImage from '../images/b3.png';
import educationLoanImage from '../images/silver.jpg';

// Define color palette
const colors = {
  primary: '#132714', // Dark green
  secondary: '#57a649', // Vibrant green
  accent: '#f5a623', // Gold accent
  lightBg: '#f8fffb', // Very light green
  textDark: '#132714',
  textLight: '#ffffff'
};

const Loans = () => {
  const loanProducts = [
    {
      icon: <Home sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Home Loans",
      description: "Finance your dream home with competitive rates and flexible repayment options tailored to your needs.",
      features: [
        "Up to 25-year repayment period",
        "Competitive interest rates",
        "100% financing available",
        "Pre-approval available"
      ],
      action: "Apply Now",
      image: homeLoanImage,
      color: 'linear-gradient(135deg, #E0E0E0 0%, #B8B8B8 100%)' // Platinum gradient
    },
    {
      icon: <DirectionsCar sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Vehicle Loans",
      description: "Get behind the wheel of your new or used vehicle with our affordable auto financing solutions.",
      features: [
        "Up to 72-month terms",
        "Quick approval process",
        "Insurance options available",
        "Low down payments"
      ],
      action: "Calculate Payment",
      image: vehicleLoanImage,
      color: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)' // Gold gradient
    },
    {
      icon: <Agriculture sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Agricultural Loans",
      description: "Specialized financing solutions designed specifically for farmers and agribusinesses.",
      features: [
        "Seasonal repayment options",
        "Equipment financing",
        "Crop insurance available",
        "Livestock financing"
      ],
      action: "Learn More",
      image: agriLoanImage,
      color: 'linear-gradient(135deg, #57a649 0%, #3d7a33 100%)' // Green gradient
    },
    {
      icon: <School sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Education Loans",
      description: "Invest in your future or your children's education with our flexible education financing.",
      features: [
        "Local and international studies",
        "Deferred payment options",
        "Competitive student rates",
        "Parent loan options"
      ],
      action: "Apply Now",
      image: educationLoanImage,
      color: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)' // Silver gradient
    }
  ];

  const loanFeatures = [
    {
      icon: <Business sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Business Growth",
      description: "Funding solutions to help your business expand"
    },
    {
      icon: <Receipt sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Flexible Terms",
      description: "Customizable repayment plans to suit your cash flow"
    },
    {
      icon: <Calculate sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Easy Calculators",
      description: "Tools to help you plan your loan repayments"
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Competitive Rates",
      description: "Affordable interest rates for all loan products"
    }
  ];

  return (
    <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: '70vh',
          minHeight: '500px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `linear-gradient(to right, rgba(19, 39, 20, 0.9), rgba(87, 166, 73, 0.7)), url(${loansHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100px',
            background: `linear-gradient(to top, ${colors.lightBg}, transparent)`,
            zIndex: 1
          }
        }}
      >
        <Container>
          <Fade in timeout={800}>
            <Box>
              <Typography 
                variant="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                <Box component="span" sx={{ color: colors.accent }}>Loan</Box> Solutions
              </Typography>
              <Typography 
                variant="h5"
                sx={{ 
                  maxWidth: '800px',
                  mx: 'auto',
                  mb: 4,
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1.1rem', md: '1.5rem' },
                  fontWeight: 300,
                  opacity: 0.9
                }}
              >
                Competitive rates and flexible terms to help you achieve your financial goals
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  size="large"
                  href="/apply"
                  sx={{ 
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    boxShadow: `0 4px 20px ${colors.secondary}80`,
                    '&:hover': {
                      boxShadow: `0 6px 25px ${colors.secondary}b3`,
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    backgroundColor: colors.secondary,
                    fontWeight: 600
                  }}
                >
                  Apply Now
                </Button>
                <Button 
                  variant="outlined" 
                  size="large"
                  href="/rates"
                  sx={{ 
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderWidth: '2px'
                    },
                    transition: 'all 0.3s ease',
                    fontWeight: 600
                  }}
                >
                  View Rates
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container sx={{ py: 10 }}>
        {/* Loan Products Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: colors.secondary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              FINANCING OPTIONS
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: colors.secondary,
                  borderRadius: '2px'
                }
              }}
            >
              Our Loan Products
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: 'text.secondary', fontWeight: 300 }}>
              Tailored financing solutions for every need
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {loanProducts.map((loan, index) => (
              <Grid item xs={12} sm={6} lg={5} key={index}>
                <Zoom in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      borderRadius: '16px',
                      overflow: 'hidden',
                      background: 'white',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      border: `1px solid ${colors.secondary}20`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                        borderColor: `${colors.secondary}40`,
                        transform: 'translateY(-10px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: '100%', md: 200 },
                        minHeight: { xs: 200, md: 'auto' },
                        background: loan.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          height: '20px',
                          background: 'linear-gradient(to bottom, transparent, white)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.3)',
                          backdropFilter: 'blur(5px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                        }}
                      >
                        {loan.icon}
                      </Box>
                    </Box>
                    <Box sx={{ p: 4, flex: 1 }}>
                      <Typography variant="h4" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                        {loan.title}
                      </Typography>
                      <Typography variant="body1" paragraph sx={{ color: 'text.secondary' }}>
                        {loan.description}
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        {loan.features.map((feature, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CheckCircle sx={{ color: colors.secondary, fontSize: 20, mr: 2 }} />
                            <Typography variant="body2" sx={{ color: colors.textDark }}>{feature}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        href={`/loans/${loan.title.toLowerCase().replace(/\s+/g, '-')}`}
                        sx={{
                          borderRadius: '50px',
                          py: 1.5,
                          backgroundColor: colors.secondary,
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#4a8f3d'
                          }
                        }}
                      >
                        {loan.action}
                      </Button>
                    </Box>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: colors.secondary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              WHY CHOOSE US
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: colors.secondary,
                  borderRadius: '2px'
                }
              }}
            >
              Loan Benefits
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: 'text.secondary', fontWeight: 300 }}>
              Advantages that set our loan products apart
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {loanFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Grow in timeout={800} style={{ transitionDelay: `${index * 150}ms` }}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      borderRadius: '16px',
                      background: 'white',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      border: `1px solid ${colors.secondary}20`,
                      transition: 'all 0.3s ease',
                      width: '100%',
                      maxWidth: 300,
                      '&:hover': {
                        boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                        borderColor: `${colors.secondary}40`,
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: `${colors.secondary}10`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Application Process */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: colors.secondary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              GET STARTED
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: colors.secondary,
                  borderRadius: '2px'
                }
              }}
            >
              Simple Application Process
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: 'text.secondary', fontWeight: 300 }}>
              Get approved in just a few steps
            </Typography>
          </Box>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: '16px',
              background: 'white',
              boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
              border: `1px solid ${colors.secondary}20`,
              maxWidth: 1200,
              mx: 'auto'
            }}
          >
            <Grid container spacing={4} justifyContent="center">
              {[
                {
                  step: "1",
                  title: "Submit Application",
                  description: "Complete our simple online form or visit a branch",
                  icon: <CheckCircle sx={{ color: colors.secondary, fontSize: 40 }} />
                },
                {
                  step: "2",
                  title: "Document Review",
                  description: "Our team will verify your information",
                  icon: <CheckCircle sx={{ color: colors.secondary, fontSize: 40 }} />
                },
                {
                  step: "3",
                  title: "Approval Decision",
                  description: "Receive a response within 48 hours",
                  icon: <CheckCircle sx={{ color: colors.secondary, fontSize: 40 }} />
                },
                {
                  step: "4",
                  title: "Funds Disbursement",
                  description: "Get your money quickly after approval",
                  icon: <CheckCircle sx={{ color: colors.secondary, fontSize: 40 }} />
                }
              ].map((step, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Fade in timeout={800} style={{ transitionDelay: `${index * 200}ms` }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          textAlign: 'center',
                          height: '100%',
                          p: 3,
                          backgroundColor: `${colors.secondary}08`,
                          borderRadius: '12px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: `0 10px 20px ${colors.secondary}20`
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: `${colors.secondary}10`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3
                          }}
                        >
                          {step.icon}
                        </Box>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            color: colors.secondary,
                            fontWeight: 700,
                            mb: 2
                          }}
                        >
                          Step {step.step}
                        </Typography>
                        <Typography variant="h5" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                          {step.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          {step.description}
                        </Typography>
                      </Box>
                    </Fade>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                href="/apply"
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  borderRadius: '50px',
                  boxShadow: `0 4px 20px ${colors.secondary}80`,
                  '&:hover': {
                    boxShadow: `0 6px 25px ${colors.secondary}b3`,
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease',
                  backgroundColor: colors.secondary,
                  fontWeight: 600
                }}
              >
                Start Your Application
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            backgroundColor: colors.primary,
            color: 'white',
            borderRadius: '16px',
            p: 8,
            mb: 6,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
              opacity: 0.3
            }
          }}
        >
          <Fade in timeout={800}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
                Ready to Apply?
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
                Start your loan application today and get a decision within 48 hours
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  href="/apply"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    boxShadow: `0 4px 20px ${colors.accent}80`,
                    backgroundColor: colors.accent,
                    color: colors.primary,
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: `0 6px 25px ${colors.accent}b3`,
                      backgroundColor: '#f5b840',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Apply Now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="/contact"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderWidth: '2px'
                    }
                  }}
                >
                  Speak to a Loan Officer
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>

        {/* Contact Section */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom sx={{ color: colors.primary, fontWeight: 700 }}>
              Loan Resources
            </Typography>
            <Grid container spacing={2}>
              {[
                "Loan Calculators",
                "Interest Rates",
                "Payment Options",
                "Document Checklist",
                "FAQ",
                "Glossary",
                "Financial Tips",
                "Contact Support"
              ].map((link, index) => (
                <Grid item xs={6} sm={4} key={index}>
                  <Button
                    href={`/loans/${link.toLowerCase().replace(/\s+/g, '-')}`}
                    sx={{ 
                      justifyContent: 'flex-start',
                      color: colors.textDark,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        color: colors.secondary,
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    {link}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: '16px',
                backgroundColor: '#f8fffb',
                border: `1px solid ${colors.secondary}20`,
                height: '100%'
              }}
            >
              <Typography variant="h3" gutterBottom sx={{ color: colors.primary, fontWeight: 700 }}>
                Loan Support
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Phone sx={{ color: colors.secondary, mr: 2 }} />
                <Typography variant="body1" sx={{ color: colors.textDark }}>
                  +268 2505 0888 (Loan Department)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Email sx={{ color: colors.secondary, mr: 2 }} />
                <Typography variant="body1" sx={{ color: colors.textDark }}>
                  loans@farmersbank.co.sz
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <LocationOn sx={{ color: colors.secondary, mr: 2 }} />
                <Typography variant="body1" sx={{ color: colors.textDark }}>
                  Mhlakuvane Street, Manzini, Eswatini
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="primary"
                href="/locations"
                endIcon={<ArrowForward />}
                sx={{
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  borderWidth: '2px',
                  borderColor: colors.secondary,
                  color: colors.secondary,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: `${colors.secondary}08`,
                    borderWidth: '2px'
                  }
                }}
              >
                Find a Branch
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Loans;